// src/components/invoices/invoice.helpers.ts
import type { InvoiceSettingsData } from '@/api/dto/settings.dto'
import type {
  CreateInvoicePayload,
  Invoice,
  InvoiceStatusUi,
} from '@/components/invoices/invoice.types'

export function formatDateDE(iso: string) {
  return new Date(iso).toLocaleDateString('de-DE')
}

export function formatEuro(cents: number) {
  const value = (cents ?? 0) / 100
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

export function periodText(fromIso: string, toIso: string) {
  return `${formatDateDE(fromIso)} - ${formatDateDE(toIso)}`
}

export function statusLabel(status: InvoiceStatusUi) {
  switch (status) {
    case 'DRAFT':
      return 'Entwurf'
    case 'FINALIZED':
      return 'Finalisiert'
    case 'SENT':
      return 'Gesendet'
    case 'ERROR':
      return 'Fehler'
    case 'ARCHIVED':
      return 'Archiviert'
    case 'CANCELED':
      return 'Storniert'
    default:
      return status
  }
}

export function statusDotColor(status: InvoiceStatusUi) {
  switch (status) {
    case 'DRAFT':
      return 'gray.solid'
    case 'FINALIZED':
      return 'yellow.solid'
    case 'SENT':
      return 'green.solid'
    case 'ERROR':
      return 'red.solid'
    case 'ARCHIVED':
      return 'gray.solid'
    case 'CANCELED':
      return 'red.solid'
    default:
      return 'gray.solid'
  }
}

export function isDraft(status: InvoiceStatusUi) {
  return status === 'DRAFT'
}
export function canDownload(status: InvoiceStatusUi) {
  // downloadable after finalize
  return (
    status === 'FINALIZED' || status === 'SENT' || status === 'ARCHIVED' || status === 'CANCELED'
  )
}
export function canWithdraw(_status: InvoiceStatusUi) {
  // not implemented yet in backend
  return false
}
export function canDownloadPreview(status: InvoiceStatusUi) {
  // no preview endpoint; use finalize+download if you want
  return status === 'DRAFT'
}
export function canEditDraft(status: InvoiceStatusUi) {
  return status === 'DRAFT'
}
export function canDeleteDraft(_status: InvoiceStatusUi) {
  // no delete endpoint in backend
  return false
}

function pad2(n: number) {
  return String(n).padStart(2, '0')
}

export function lastDayOfMonth(year: number, month1to12: number) {
  // JS Date month is 0-based; day=0 gives last day of previous month
  return new Date(year, month1to12, 0).getDate()
}

export function toPeriod(year: number, month1to12: number) {
  const m = pad2(month1to12)
  const start = `${year}-${m}-01`
  const end = `${year}-${m}-${pad2(lastDayOfMonth(year, month1to12))}`
  return { periodStart: start, periodEnd: end }
}

/**
 * Convert YYYY-MM-DD period bounds into local Date bounds covering the whole days:
 * start-of-day for periodStart and end-of-day for periodEnd.
 * Parsing the strings with `new Date()` would yield midnight UTC, which excludes
 * everything on the last day of the period.
 */
export function periodToDateRange(periodStart: string, periodEnd: string) {
  const [sy, sm, sd] = periodStart.split('-').map(Number)
  const [ey, em, ed] = periodEnd.split('-').map(Number)
  const from = new Date(sy, sm - 1, sd, 0, 0, 0, 0)
  const to = new Date(ey, em - 1, ed, 23, 59, 59, 999)
  return { from, to }
}

export function lineTotalCents(l: { menge: unknown; preisProEinheitCents: unknown }) {
  const count = Number(l.menge) || 0
  const price = Number(l.preisProEinheitCents) || 0
  return count * price
}

export function validateMandatoryInvoiceSettings(s: InvoiceSettingsData | null): {
  complete: boolean
  missing: string[]
} {
  if (!s) return { complete: false, missing: ['Rechnungseinstellungen'] }

  const missing: string[] = []
  const reqStr = (v: unknown) => typeof v === 'string' && v.trim().length > 0
  const reqVatPct = (v: unknown) =>
    typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100

  const issuerType = (s.issuerType ?? 'PERSON') as 'COMPANY' | 'PERSON' | 'COMPANY_PERSON'
  const companyRequired = issuerType === 'COMPANY' || issuerType === 'COMPANY_PERSON'
  const personRequired = issuerType === 'PERSON' || issuerType === 'COMPANY_PERSON'

  // issuer selection
  if (!reqStr(issuerType)) missing.push('Rechnungssteller (Typ)')

  // issuer identity
  if (companyRequired && !reqStr(s.company)) missing.push('Unternehmen')
  if (personRequired && !reqStr(s.firstName)) missing.push('Vorname')
  if (personRequired && !reqStr(s.lastName)) missing.push('Nachname')

  // address
  if (!reqStr(s.street)) missing.push('Straße')
  if (!reqStr(s.streetNumber)) missing.push('Hausnummer')
  if (!reqStr(s.postal)) missing.push('Postleitzahl')
  if (!reqStr(s.city)) missing.push('Stadt')

  // payment/bank
  if (!reqStr(s.accountHolder)) missing.push('Kontoinhaber')
  if (!reqStr(s.bankName)) missing.push('Bankname')
  if (!reqStr(s.iban)) missing.push('IBAN')
  if (!reqStr(s.bic)) missing.push('BIC')

  // invoice basics
  if (!reqVatPct(s.vatPercentage)) missing.push('MwSt. (%)')
  if (!reqStr(s.invoiceNumberPrefix)) missing.push('Rechnungsnummernserie')

  // auto invoice only if enabled
  if (s.autoGenerationEnabled) {
    if (!s.autoGenerationDay) missing.push('Auto-Rechnung: Tag')
    if (!reqStr(s.autoGenerationTime)) missing.push('Auto-Rechnung: Uhrzeit')
  }

  return { complete: missing.length === 0, missing }
}

export function sanitizeFilename(name: string) {
  // windows + general filesystem safe
  return (name ?? '').replace(/[\\/:*?"<>|]/g, '-').trim()
}

export function invoiceDownloadFilename(
  invoiceNumberOrFallback: string,
  ext: 'pdf' | 'xml' | 'zip'
) {
  const base = sanitizeFilename(invoiceNumberOrFallback) || 'invoice'
  return `${base}.${ext}`
}

export function scopeLabel(scope?: string) {
  switch ((scope ?? '').toUpperCase()) {
    case 'ACTIVITY':
      return { label: 'Aktivität', colorPalette: 'blue' as const }
    case 'ORDER':
      return { label: 'Bestellung', colorPalette: 'green' as const }
    default:
      return null
  }
}

export type UnionAssociatedEntity = {
  id: number
  name: string
  name2?: string | null
  name3?: string | null
}

export function resolveInvoiceLineCustomerName(
  line: { customerId?: number | string },
  unionAssociatedEntities: UnionAssociatedEntity[],
  fallbackName?: string
) {
  const customerId = Number((line as any)?.customerId ?? 0)

  if (customerId > 0) {
    const hit = unionAssociatedEntities.find(e => Number(e.id) === customerId)
    if (hit) return formatEntityLabel(hit.name, hit.name2, hit.name3)
    return fallbackName ?? `#${customerId}`
  }

  return fallbackName ?? '—'
}

export function formatEntityLabel(
  name?: string | null,
  name2?: string | null,
  name3?: string | null
) {
  return [name, name2, name3].filter(Boolean).join(' – ')
}

export function resolveInvoiceCardTitle(invoice: Invoice, unionEntities: UnionAssociatedEntity[]) {
  const isOrder = (invoice.scope ?? invoice.type)?.toUpperCase() === 'ORDER'
  if (!isOrder) return invoice.bankName

  const customerId = Number(invoice.customerId ?? 0)
  if (customerId > 0) {
    return resolveInvoiceLineCustomerName(
      { customerId },
      unionEntities,
      invoice.bankName // fallback if not found
    )
  }

  // customerId = 0 → show bankName
  return invoice.bankName
}

export function resolveInvoiceHeaderTitle(
  invoice: Invoice,
  unionEntities: UnionAssociatedEntity[]
) {
  const isOrder = invoice.scope === 'ORDER'
  if (!isOrder) return invoice.bankName

  const customerId = Number(invoice.customerId ?? 0)

  // fallback: customerId = 0 → bankName
  if (customerId <= 0) return invoice.bankName

  return resolveInvoiceLineCustomerName({ customerId }, unionEntities, invoice.bankName)
}

type Locale = 'de' | 'en'
export function getLocale(): Locale {
  const v = (
    localStorage.getItem('i18nextLng') ||
    document.documentElement.lang ||
    navigator.language ||
    'de'
  )?.toLowerCase()
  return v.startsWith('de') ? 'de' : 'en'
}

export function localizeCancellationComment(comment: string | undefined | null) {
  const c = (comment ?? '').trim()
  if (!c) return ''

  // tolerate casing, ":" and extra spaces; capture invoice number/id part
  const m = c.match(/^\s*cancellation of invoice\s*:?\s*(.+?)\s*\.?\s*$/i)
  if (!m) return comment ?? ''

  const invNo = m[1].trim()
  return `Stornierung der Rechnung ${invNo}`
}

// Some backend invoice errors arrive in English. Map the known ones to German so the
// user never sees an English toast. Returns undefined when there is no known match,
// so callers can fall back to the raw message.
// `message` may be a plain string or a builder that receives the regex match, so messages
// carrying a backend-supplied detail (e.g. an activity id) can keep that detail in German.
type InvoiceErrorTranslation = {
  match: RegExp
  message: string | ((match: RegExpMatchArray) => string)
}

const INVOICE_ERROR_TRANSLATIONS: InvoiceErrorTranslation[] = [
  {
    match: /invoice number is required when automatic invoice creation is disabled/i,
    message:
      'Bitte geben Sie eine Rechnungsnummer ein, da die automatische Rechnungserstellung deaktiviert ist.',
  },
  {
    // e.g. "Activity 198 is not for the specified bank" (SIB-859): the selected activity
    // belongs to a different Auftraggeber than the one the invoice is being created for.
    match: /Activity (\d+) is not for the specified bank/i,
    message: m =>
      `Die Aktivität ${m[1]} gehört nicht zum ausgewählten Auftraggeber. Bitte entfernen Sie sie aus der Auswahl oder wählen Sie den passenden Auftraggeber.`,
  },
]

export function translateInvoiceErrorMessage(raw?: string | null): string | undefined {
  const msg = (raw ?? '').trim()
  if (!msg) return undefined

  for (const translation of INVOICE_ERROR_TRANSLATIONS) {
    const match = msg.match(translation.match)
    if (match) {
      return typeof translation.message === 'function'
        ? translation.message(match)
        : translation.message
    }
  }

  return undefined
}

export function maskInvoiceNumberPattern(
  template?: string,
  prefix?: string,
  year?: number,
  month?: number
) {
  if (!template) return ''

  const yyyy = String(year ?? new Date().getFullYear())
  const mm = String(month ?? new Date().getMonth() + 1).padStart(2, '0')

  return template
    .replace('{PREFIX}', prefix ?? '')
    .replace('{YYYY}', yyyy)
    .replace('{YY}', yyyy.slice(-2))
    .replace('{MM}', mm)
    .replace('{RUNNING}', 'xx')
}

const BILLING_VARIANT_DE: Record<string, string> = {
  STANDARD: 'Standard',
  EIGHTY_PERCENT_TOJ: '80 % (ToJ)',
  EIGHTY_PERCENT_TRAINING: '80 % (Schulung)',
  EIGHTY_PERCENT_CONSULTING: '80 % (Beratung)',
  EIGHTY_PERCENT_PILOT: '80 % (Pilot)',
  HALF_TRAINING_DAY: 'Halber Schulungstag',
  HALF_HOUR_CONSULTING: 'Halbe Einheit (Beratung)',
}

export function formatBillingVariant(v?: string | null) {
  if (!v) return 'Standard' // ✅ fallback for old invoices
  return BILLING_VARIANT_DE[v] ?? v
}

export function isCreditNote(invoice: Invoice) {
  // safest current approximation with existing backend fields
  return !!invoice.canceledInvoiceId || !!invoice.comment?.toLowerCase().includes('storn')
}

export function matchesInvoiceStatusTab(
  invoice: Invoice,
  statusTab: 'vorschau' | 'erstellt' | 'versendet' | 'storniert' | null
) {
  if (!statusTab) return true

  switch (statusTab) {
    case 'vorschau':
      return invoice.status === 'DRAFT' && invoice.approvalStatus !== 'PENDING_APPROVAL'
    case 'erstellt':
      return (
        invoice.status === 'FINALIZED' ||
        invoice.approvalStatus === 'PENDING_APPROVAL' ||
        invoice.approvalStatus === 'REJECTED'
      )
    case 'versendet':
      return invoice.status === 'SENT'
    case 'storniert':
      return invoice.status === 'CANCELED'
    default:
      return true
  }
}

export function matchesInvoiceCategoryTab(
  invoice: Invoice,
  categoryTab: 'alle' | 'meine-rechnungen' | 'gutschriften'
) {
  switch (categoryTab) {
    case 'alle':
      return true
    case 'meine-rechnungen':
      return !isCreditNote(invoice)
    case 'gutschriften':
      return isCreditNote(invoice)
    default:
      return true
  }
}

export function invoiceScopeLabel(scope?: string) {
  switch ((scope ?? '').toUpperCase()) {
    case 'ACTIVITY':
      return 'Aktivität'
    case 'ORDER':
      return 'Bestellung'
    case 'MEDIATION':
      return 'Vermittlung'
    default:
      return 'Rechnung'
  }
}

export function invoiceMainStatusPill(invoice: Invoice) {
  if (invoice.status === 'CANCELED') {
    return { label: 'Storniert', colorPalette: 'red' as const }
  }

  if (invoice.approvalStatus === 'PENDING_APPROVAL') {
    return { label: 'Vorschau', colorPalette: 'gray' as const }
  }

  if (invoice.approvalStatus === 'REJECTED') {
    return { label: 'Abgelehnt', colorPalette: 'red' as const }
  }

  if (invoice.approvalStatus === 'APPROVED' && invoice.status === 'SENT') {
    return { label: 'Freigegeben & Ausgestellt', colorPalette: 'green' as const }
  }

  switch (invoice.status) {
    case 'DRAFT':
      return { label: 'Vorschau', colorPalette: 'gray' as const }
    case 'FINALIZED':
      return { label: 'Ausgestellt', colorPalette: 'green' as const }
    case 'SENT':
      return { label: 'Ausgestellt', colorPalette: 'green' as const }
    default:
      return { label: invoice.status, colorPalette: 'gray' as const }
  }
}

export function invoiceDeliveryStatusPill(invoice: Invoice) {
  if (invoice.status === 'CANCELED') return null

  if (invoice.approvalStatus === 'PENDING_APPROVAL') {
    return { label: 'Wartet auf Freigabe', colorPalette: 'yellow' as const }
  }

  if (invoice.approvalStatus === 'REJECTED') {
    return { label: 'Ablehnungsgrund prüfen', colorPalette: 'red' as const }
  }

  if (invoice.approvalStatus === 'APPROVED' && invoice.status === 'SENT') {
    return { label: 'Automatisch versendet', colorPalette: 'green' as const }
  }

  switch (invoice.status) {
    case 'FINALIZED':
      return { label: 'Nicht versendet', colorPalette: 'green' as const }
    case 'SENT':
      return { label: 'Versendet', colorPalette: 'green' as const }
    default:
      return null
  }
}
export function isUnionInvoiceBank(bankId?: number | string) {
  return Number(bankId) === 1
}
export function buildEffectivePeriod(values: CreateInvoicePayload) {
  if (values.mode === 'MONTH') {
    return toPeriod(Number(values.billingYear), Number(values.billingMonth))
  }

  if (values.mode === 'DATE_RANGE') {
    return {
      periodStart: String(values.periodStart),
      periodEnd: String(values.periodEnd),
    }
  }

  // activities mode: fallback to current visible activity date range
  // const ids = values.selectedActivityIds ?? []
  return {
    periodStart: values.periodStart || '',
    periodEnd: values.periodEnd || '',
  }
}

export function activityTypeToTitle(type?: string): string {
  const normalized = String(type ?? '')
    .trim()
    .toUpperCase()
    .replace(/[-\s]/g, '_')

  switch (normalized) {
    case 'TRAINING':
      return 'Schulung'
    case 'CONSULTING':
      return 'Beratung'
    case 'TOJ':
      return 'TOJ'
    case 'TOJ_CUSTOMER':
      return 'TOJ mit Kunde'
    case 'PILOT':
      return 'Pilot'
    default:
      return 'Aktivität'
  }
}

export function isSameMonthRange(periodStart: string, periodEnd: string) {
  if (!periodStart || !periodEnd) return false

  const start = new Date(periodStart)
  const end = new Date(periodEnd)

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false

  const sameYear = start.getFullYear() === end.getFullYear()
  const sameMonth = start.getMonth() === end.getMonth()
  const startsOnFirst = start.getDate() === 1
  const endsOnLast = end.getDate() === lastDayOfMonth(end.getFullYear(), end.getMonth() + 1)

  return sameYear && sameMonth && startsOnFirst && endsOnLast
}

export function detectEditMode(draftInvoice: Invoice): CreateInvoicePayload['mode'] {
  const itemDates = (draftInvoice.items ?? [])
    .map(it => String(it.activityDate ?? ''))
    .filter(Boolean)

  if (itemDates.length > 0) return 'ACTIVITY_SELECTION'

  const periodStart = draftInvoice.periodStart || draftInvoice.periodFrom
  const periodEnd = draftInvoice.periodEnd || draftInvoice.periodTo

  if (isSameMonthRange(periodStart, periodEnd)) return 'MONTH'
  return 'DATE_RANGE'
}
